import * as React from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Toast } from 'zarm';
import { events } from '@iyunbao/utils';

import { difference, diffchange } from '../utils/tools';
import Hr from '../hr';

import {
  BaseWidgetProps,
} from '../utils/type';

type OptionsProps = Array<{
  label: string;
  key: string | number;
}>;

export interface TabsProps extends BaseWidgetProps {
  defaultValue?: number;
  options: OptionsProps;
  type?: 'normal' | 'slide' | 'move';
  canSwipe?: boolean;
  headerHidden?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface TabsState {
  current: number;
  canMove?: boolean;
  isTabFix?: boolean;
}

const getValue = (props: TabsProps) => {
  const { defaultValue, type } = props;
  const state = {
    current: 0,
    canMove: false,
  };

  if (typeof defaultValue === 'number') {
    state.current = defaultValue;
  }

  if (type && type === 'move') {
    state.canMove = true;
  }

  return state;
};

let timer: number | null = null;

const diffOmitList: string[] = [
  'prefixCls',
  'widget',
  'children',
  '_crengine',
];

class UTabs extends React.PureComponent<TabsProps, TabsState> {
  static propTypes = {
    prefixCls: PropTypes.string,
    // defaultValue: PropTypes.number,
    headerHidden: PropTypes.bool,
    type: PropTypes.oneOf(['normal', 'slide', 'move']),
    options: PropTypes.arrayOf(PropTypes.any),
  };

  static defaultProps = {
    prefixCls: 'crm-tabs',
    // defaultValue: 0,
    headerHidden: false,
    type: 'normal',
    options: [],
  };

  state: TabsState = {
    ...getValue(this.props),
  };

  isTabClick: boolean = false;

  tabsRef: any = React.createRef();

  tabsHeaderRef: any = React.createRef();

  topsTop: number = 0;

  // 内容元素
  itemRef: any[] = [];

  // 内容位置
  itemTop: any[] = [];

  componentDidMount() {
    const { canMove } = this.state;

    if (canMove) {
      this.getTabTop();
      this.getContentTop();

      events.on(window, 'scroll', this.onScroll);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps: TabsProps, props) {
    const { canMove } = this.state;
    const list: string[] = difference(Object.keys(nextProps), diffOmitList);

    // diff key name || diff value
    if (
      (
        list.length !== difference(Object.keys(props), diffOmitList).length
        || diffchange(list, nextProps, props)
      )
      && canMove
    ) {
      this.getTabTop();
      this.getContentTop();
    }
  }

  componentWillUnmount() {
    try {
      events.off(window, 'scroll', this.onScroll);
    } catch (error) {
      console.error(error);
    }
  }

  onScroll = () => {
    let { isTabFix, current } = this.state;
    const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
    const len = this.itemTop.length;

    if (scrollTop >= this.topsTop) {
      isTabFix = true;
    } else {
      isTabFix = false;
    }

    if (len) {
      this.itemTop.map((item, index) => {
        if (!this.isTabClick && item.top <= scrollTop && (index >= len - 1 || (item.top + item.height) > scrollTop)) {
          current = index;
        }
        return item;
      });
    }

    if (this.state.isTabFix !== isTabFix || this.state.current !== current) {
      this.setState({
        isTabFix,
        current,
      });
    }
  };

  onChangeTab = (index: number) => {
    const { canMove } = this.state;

    if (canMove) {
      const len = this.itemTop.length;

      this.isTabClick = true;

      timer = setTimeout(() => {
        timer && clearTimeout(timer);
        this.isTabClick = false;
      }, 300);

      if (len && index <= len - 1) {
        const { top } = this.itemTop[index];
        window.scrollTo(0, top);
      } else {
        Toast.show({
          content: '请等待图片加载完成，再点击不然定位不准哦！',
          mask: true,
        });
      }
    }

    this.setState({
      current: index,
    });
  };

  onChange = (item: any) => {
    const { onChange } = this.props;

    onChange && onChange(item);
  };

  // 获取tab元素
  getTabsElems = () => {
    if (this.tabsRef && this.tabsRef.current) {
      // eslint-disable-next-line react/no-find-dom-node
      return findDOMNode(this.tabsRef.current);
    }

    return null;
  };

  // 获取高度
  getTabsHeaderHeight = () => {
    if (this.tabsHeaderRef && this.tabsHeaderRef.current) {
      // eslint-disable-next-line react/no-find-dom-node
      const headerElem: any = findDOMNode(this.tabsHeaderRef.current);
      return headerElem.height || 40;
    }

    return 0;
  };

  getTabTop = () => {
    const { options, headerHidden } = this.props;
    const tabsElem: any = this.getTabsElems();

    if (!(tabsElem && Array.isArray(options) && options.length)) {
      return false;
    }

    const headerHeight = (headerHidden ? 0 : this.getTabsHeaderHeight());

    this.topsTop = ((tabsElem.getBoundingClientRect().top + window.scrollY + headerHeight) || 0);
  };

  // 内容top
  getContentTop = () => {
    const tabsElem = this.getTabsElems();

    if (!tabsElem) {
      return false;
    }

    if (this.itemRef && Array.isArray(this.itemRef) && this.itemRef.length) {
      this.imageComplete(tabsElem)
        .then(() => {
          const itemTop: any[] = [];

          this.itemRef.map((elem) => {
            itemTop.push({
              top: ((elem.getBoundingClientRect().top + window.scrollY - this.getTabsHeaderHeight()) || 0),
              height: (elem.getBoundingClientRect().height || 0),
            });

            return elem;
          });

          this.itemTop = itemTop;
        });
    }
  };

  setRef = (ref: any) => {
    if (ref) {
      this.itemRef.push(ref);
    }
  };

  imageComplete = (tabsNode: any) => {
    return new Promise((resolve) => {
      const imageElem = tabsNode.getElementsByTagName('img');
      const len = imageElem.length;
      let number = 0;

      if (!len) {
        resolve();
        return false;
      }

      const loadResult = () => {
        number += 1;

        if (number >= len) {
          timer && clearTimeout(timer);
          resolve();
        }
      };

      for (let i = 0; i < len; i += 1) {
        const img = new Image();

        img.onload = () => loadResult();
        img.onerror = () => loadResult();

        img.src = imageElem[i].src;
      }

      // 图片加载超时
      timer = setTimeout(() => {
        resolve();
      }, 2000);
    });
  };

  renderContent() {
    const { current, canMove } = this.state;
    const { prefixCls, children } = this.props;

    if (children && Array.isArray(children) && children.length) {
      const len = children.length;

      return children.map((child, index) => {
        return (
          <div
            className={classnames(`${prefixCls}-panel`, {
              [`${prefixCls}-panel-show`]: (!canMove && index === current),
            })}
            ref={this.setRef}
            key={`panel_${index}`}
          >
            {child}
            {(canMove && index < len - 1) ? <Hr /> : null}
          </div>
        );
      });
    }

    return null;
  }

  render() {
    const { current, isTabFix } = this.state;
    const {
      prefixCls,
      options,
      type = 'normal',
      headerHidden = false,
      className,
    } = this.props;
    const cls = classnames(prefixCls, `${prefixCls}-${type}`, className, {
      [`${prefixCls}-header-hidden`]: headerHidden,
      [`${prefixCls}-fixed`]: isTabFix,
    });

    if (!(options && Array.isArray(options) && options.length)) {
      return null;
    }
    const len = options.length;
    const lineStyle = {
      width: `${100 / len}%`,
      left: `${(current / len) * 100}%`,
    };

    return (
      <div className={cls} ref={this.tabsRef}>
        <div
          className={`${prefixCls}-header`}
          ref={this.tabsHeaderRef}
        >
          <div className={`${prefixCls}-header-inner`}>
            <ul className="tab-list">
              {
                options.map(({ label, key }, index) => (
                  <li
                    className={classnames({
                      active: (index === current),
                    })}
                    key={`tabs_${key}_${index}`}
                    onClick={() => this.onChangeTab(index)}
                  >
                    {label}
                  </li>
                ))
              }
            </ul>
            <div className={`${prefixCls}-line`} style={lineStyle} />
          </div>
        </div>

        <div className={`${prefixCls}-body`}>
          {this.renderContent()}
        </div>
      </div>
    );
  }
}

export default UTabs;
